import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import jsPDF from "jspdf";
import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    ResponsiveContainer, Legend, Tooltip
} from "recharts";
import {
    components, likertScale, levels, getLevel, stages, scoreComponent, overallScore
} from "../data/competencyData";
import { loadAll, saveResult, studentKey, removeStudent } from "../data/diagnosticsStore";
import GrowthPlan from "../components/GrowthPlan";
import StudentHeader from "../components/StudentHeader";

const inputStyle = { padding: "12px", border: "1px solid #ddd", borderRadius: "8px", width: "100%" };

function LevelBadge({ percent }) {
    const level = getLevel(percent);
    return (
        <span style={{ background: level.color, color: "#fff", padding: "3px 12px", borderRadius: "20px", fontSize: "14px", fontWeight: 600 }}>
            {level.label}
        </span>
    );
}

function Diagnostics() {
    const [params] = useSearchParams();
    const [stage, setStage] = useState(params.get("stage") === "post" ? "post" : "pre");
    const [name, setName] = useState("");
    const [group, setGroup] = useState("");
    const [started, setStarted] = useState(false);
    const [answers, setAnswers] = useState({});
    const [record, setRecord] = useState(null);
    const [all, setAll] = useState(loadAll);

    const existing = name.trim() ? all[studentKey(name, group)] : null;
    const totalItems = components.reduce((acc, c) => acc + c.items.length, 0);
    const answeredCount = components.reduce((acc, c) => acc + Object.keys(answers[c.id] || {}).length, 0);

    const setAnswer = (compId, i, value) =>
        setAnswers({ ...answers, [compId]: { ...(answers[compId] || {}), [i]: value } });

    const submit = () => {
        const scores = {};
        components.forEach((c) => { scores[c.id] = scoreComponent(c, answers[c.id]); });
        const result = { scores, overall: overallScore(scores), date: new Date().toISOString() };
        setRecord(saveResult(name, group, stage, result));
        setAll(loadAll());
        window.scrollTo(0, 0);
    };

    const restart = () => {
        setStarted(false);
        setAnswers({});
        setRecord(null);
    };

    const downloadPdf = () => {
        const doc = new jsPDF({ format: "a4" });
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text("Kasbiy-metodik kompetensiya diagnostikasi", 105, 22, { align: "center" });
        doc.setFontSize(11);
        doc.setFont(undefined, "normal");
        doc.text(`Talaba: ${record.name}`, 20, 38);
        doc.text(`Guruh: ${record.group || "-"}`, 20, 45);

        let y = 60;
        doc.setFont(undefined, "bold");
        doc.text("Komponent", 20, y);
        ["pre", "post"].forEach((s, i) => doc.text(stages[s].short, 120 + i * 30, y));
        doc.text("O'sish", 180, y);
        doc.setFont(undefined, "normal");
        doc.line(20, y + 2, 195, y + 2);

        const rows = [...components.map((c) => ({ label: c.title, key: c.id })), { label: "Umumiy ko'rsatkich", key: "overall" }];
        rows.forEach((r) => {
            y += 9;
            const val = (s) => {
                if (!record[s]) return null;
                return r.key === "overall" ? record[s].overall : record[s].scores[r.key];
            };
            doc.text(r.label, 20, y);
            ["pre", "post"].forEach((s, i) => {
                const v = val(s);
                doc.text(v === null ? "-" : `${v}% (${getLevel(v).label})`, 120 + i * 30, y);
            });
            if (val("pre") !== null && val("post") !== null) {
                const d = val("post") - val("pre");
                doc.text(`${d > 0 ? "+" : ""}${d}%`, 180, y);
            }
        });

        y += 16;
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("Darajalar: past (0-54%), o'rta (55-79%), yuqori (80-100%).", 20, y);
        ["pre", "post"].forEach((s) => {
            if (record[s]) {
                y += 6;
                doc.text(`${stages[s].label}: ${new Date(record[s].date).toLocaleDateString("uz-UZ")}`, 20, y);
            }
        });
        doc.text("Mushtariy platformasi", 105, 285, { align: "center" });
        doc.save(`diagnostika_${record.name.replace(/\s+/g, "_")}.pdf`);
    };

    const exportCsv = () => {
        const header = ["F.I.Sh", "Guruh"];
        ["pre", "post"].forEach((s) => {
            components.forEach((c) => header.push(`${stages[s].short}: ${c.short}`));
            header.push(`${stages[s].short}: umumiy`, `${stages[s].short}: daraja`, `${stages[s].short}: sana`);
        });
        const lines = Object.values(all).map((r) => {
            const row = [r.name, r.group];
            ["pre", "post"].forEach((s) => {
                const st = r[s];
                components.forEach((c) => row.push(st ? st.scores[c.id] : ""));
                row.push(st ? st.overall : "", st ? getLevel(st.overall).label : "", st ? new Date(st.date).toLocaleDateString("uz-UZ") : "");
            });
            return row;
        });
        const csv = [header, ...lines]
            .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
            .join("\n");
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `diagnostika_natijalari_${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    // Natija ekrani
    if (record) {
        const current = record[stage];
        const hasBoth = record.pre && record.post;
        const radarData = components.map((c) => ({
            metric: c.short,
            [stages.pre.short]: record.pre ? record.pre.scores[c.id] : 0,
            [stages.post.short]: record.post ? record.post.scores[c.id] : 0,
        }));
        return (
            <>
                <StudentHeader title={`${stages[stage].label} natijasi`} crumb="Diagnostika" />
                <section className="pricing-one section-space-bottom">
                    <div className="container">
                        <div className="pricing-one__main-tab-box tabs-box">
                            <div className="pricing-one__inner px-3 px-lg-5" style={{ paddingTop: "40px", paddingBottom: "40px" }}>
                                <div className="text-center mb-5">
                                    <p style={{ color: "#666", marginBottom: 5 }}>{record.name}{record.group ? ` · ${record.group}` : ""}</p>
                                    <h1 style={{ fontSize: "60px", color: "#ffb606", margin: "10px 0" }}>{current.overall}%</h1>
                                    <p style={{ fontSize: "18px" }}>Umumiy kompetensiya darajasi: <LevelBadge percent={current.overall} /></p>
                                </div>

                                <div className="row gutter-y-30">
                                    <div className={hasBoth ? "col-lg-6" : "col-12"}>
                                        {components.map((c) => {
                                            const v = current.scores[c.id];
                                            const prev = stage === "post" && record.pre ? record.pre.scores[c.id] : null;
                                            return (
                                                <div key={c.id} style={{ marginBottom: "22px" }}>
                                                    <div className="d-flex justify-content-between align-items-center" style={{ gap: 10, flexWrap: "wrap" }}>
                                                        <strong>{c.title}</strong>
                                                        <span>
                                                            {v}% <LevelBadge percent={v} />
                                                            {prev !== null && (
                                                                <span style={{ marginLeft: 8, color: v - prev >= 0 ? "#10b981" : "#ef4444", fontWeight: 600 }}>
                                                                    {v - prev > 0 ? "+" : ""}{v - prev}%
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div style={{ background: "#eee", borderRadius: 8, height: 10, marginTop: 8 }}>
                                                        <div style={{ width: `${v}%`, background: getLevel(v).color, height: "100%", borderRadius: 8 }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {hasBoth && (
                                        <div className="col-lg-6">
                                            <h4 className="text-center">Kirish va chiqish taqqoslamasi</h4>
                                            <ResponsiveContainer width="100%" height={320}>
                                                <RadarChart data={radarData}>
                                                    <PolarGrid />
                                                    <PolarAngleAxis dataKey="metric" />
                                                    <PolarRadiusAxis domain={[0, 100]} />
                                                    <Radar name={stages.pre.short} dataKey={stages.pre.short} stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.3} />
                                                    <Radar name={stages.post.short} dataKey={stages.post.short} stroke="#ffb606" fill="#ffb606" fillOpacity={0.4} />
                                                    <Legend />
                                                    <Tooltip />
                                                </RadarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-4">
                                    <GrowthPlan scores={current.scores} />
                                </div>

                                {stage === "pre" && (
                                    <p className="text-center mt-4" style={{ color: "#666" }}>
                                        Endi <Link to="/students" style={{ color: "#ffb606" }}>o'quv yo'li</Link> bo'yicha metodlarni o'rganing.
                                        Yakunida xuddi shu F.I.Sh va guruh bilan chiqish diagnostikasini topshiring.
                                    </p>
                                )}

                                <div className="d-flex justify-content-center" style={{ gap: "15px", marginTop: "30px", flexWrap: "wrap" }}>
                                    <button onClick={downloadPdf} className="eduhive-btn" style={{ border: "none" }}>
                                        <span>Natijani PDF yuklab olish</span>
                                        <span className="eduhive-btn__icon">
                                            <span className="eduhive-btn__icon__inner"><i className="icon-right-arrow"></i></span>
                                        </span>
                                    </button>
                                    <Link to="/students" className="eduhive-btn" style={{ background: "#666" }}>
                                        <span>Talabalar bo'limiga qaytish</span>
                                    </Link>
                                    <button onClick={restart} className="eduhive-btn" style={{ border: "none", background: "#666" }}>
                                        <span>Yangi diagnostika</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    // So'rovnoma ekrani
    if (started) {
        return (
            <>
                <StudentHeader title={stages[stage].label} crumb="Diagnostika" />
                <section className="pricing-one section-space-bottom">
                    <div className="container">
                        <div className="pricing-one__main-tab-box tabs-box">
                            <div className="pricing-one__inner px-3 px-lg-5">
                                <div style={{ position: "sticky", top: 90, zIndex: 5, background: "#fff", padding: "12px 0", marginBottom: 20 }}>
                                    <div className="d-flex justify-content-between" style={{ fontSize: 14 }}>
                                        <span>{name}{group ? ` · ${group}` : ""}</span>
                                        <span>{answeredCount} / {totalItems}</span>
                                    </div>
                                    <div style={{ background: "#eee", borderRadius: 8, height: 6, marginTop: 6 }}>
                                        <div style={{ width: `${(answeredCount / totalItems) * 100}%`, background: "#ffb606", height: "100%", borderRadius: 8, transition: "width 0.3s" }} />
                                    </div>
                                </div>

                                {components.map((c, ci) => (
                                    <div key={c.id} style={{ marginBottom: "40px" }}>
                                        <h3 style={{ marginBottom: 6 }}>{ci + 1}. {c.title}</h3>
                                        <p style={{ color: "#666", marginBottom: 20 }}>
                                            {c.type === "likert"
                                                ? "Har bir fikrga qanchalik qo'shilishingizni belgilang. To'g'ri yoki noto'g'ri javob yo'q."
                                                : c.id === "activity"
                                                    ? "Pedagogik vaziyatni o'qing va eng to'g'ri metodik harakatni tanlang."
                                                    : "To'g'ri javobni tanlang."}
                                        </p>
                                        {c.items.map((item, i) => {
                                            const selected = (answers[c.id] || {})[i];
                                            const options = c.type === "likert" ? likertScale : item.options;
                                            return (
                                                <div key={i} style={{ background: "#fafafa", padding: "20px", borderRadius: "12px", marginBottom: "16px", borderLeft: "4px solid #ffb606" }}>
                                                    <h5 style={{ marginBottom: "12px" }}>{i + 1}. {c.type === "likert" ? item : item.q}</h5>
                                                    <div style={c.type === "likert" ? { display: "flex", flexWrap: "wrap", gap: 8 } : undefined}>
                                                        {options.map((opt, oi) => {
                                                            const value = c.type === "likert" ? oi + 1 : oi;
                                                            const active = selected === value;
                                                            return (
                                                                <label key={oi} style={{
                                                                    display: "block",
                                                                    flex: c.type === "likert" ? "1 1 150px" : undefined,
                                                                    padding: "10px 15px",
                                                                    margin: c.type === "likert" ? 0 : "5px 0",
                                                                    background: active ? "#ffb606" : "#fff",
                                                                    color: active ? "#fff" : "#333",
                                                                    borderRadius: "8px",
                                                                    cursor: "pointer",
                                                                    border: "1px solid #e5e5e5",
                                                                    fontSize: c.type === "likert" ? 14 : undefined,
                                                                    transition: "all 0.2s"
                                                                }}>
                                                                    <input
                                                                        type="radio"
                                                                        name={`${c.id}-${i}`}
                                                                        checked={active}
                                                                        onChange={() => setAnswer(c.id, i, value)}
                                                                        style={{ marginRight: "10px" }}
                                                                    />
                                                                    {opt}
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))}

                                <div className="text-center mt-5">
                                    <button
                                        onClick={submit}
                                        disabled={answeredCount < totalItems}
                                        className="eduhive-btn"
                                        style={{ border: "none", opacity: answeredCount < totalItems ? 0.5 : 1 }}
                                    >
                                        <span>Yakunlash va natijani ko'rish</span>
                                        <span className="eduhive-btn__icon">
                                            <span className="eduhive-btn__icon__inner"><i className="icon-right-arrow"></i></span>
                                        </span>
                                    </button>
                                    {answeredCount < totalItems && (
                                        <p style={{ color: "#999", marginTop: 10, fontSize: 14 }}>Barcha savollarga javob bering ({totalItems - answeredCount} ta qoldi).</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    // Kirish ekrani
    const records = Object.entries(all);
    const distribution = (s) => {
        const done = records.filter(([, r]) => r[s]);
        return levels.map((l) => {
            const count = done.filter(([, r]) => getLevel(r[s].overall).id === l.id).length;
            return { ...l, count, percent: done.length ? Math.round((count / done.length) * 100) : 0 };
        });
    };

    return (
        <>
            <StudentHeader title="Kasbiy-metodik kompetensiya diagnostikasi" crumb="Diagnostika" />
            <section className="pricing-one section-space-bottom">
                <div className="container">
                    <div className="pricing-one__main-tab-box tabs-box">
                        <div className="pricing-one__top">
                            <div className="pricing-one__content">
                                <h3 className="pricing-one__title">Bo'lajak tarbiyachilar uchun</h3>
                                <p className="pricing-one__text">
                                    Diagnostika 4 ta komponentdan iborat ({totalItems} ta savol, taxminan 15 daqiqa).
                                    Kirish va chiqish bosqichida bir xil savollar beriladi — shu orqali o'sish dinamikangiz aniqlanadi.
                                </p>
                            </div>
                        </div>
                        <div className="pricing-one__inner px-3 px-lg-5">
                            <div className="row gutter-y-30">
                                <div className="col-lg-6">
                                    <div className="mb-3">
                                        <label className="d-block mb-2"><strong>Familiya, ism</strong></label>
                                        <input type="text" placeholder="masalan: Aliyeva Malika" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="d-block mb-2"><strong>Guruh / kurs</strong></label>
                                        <input type="text" placeholder="masalan: MTT-301" style={inputStyle} value={group} onChange={(e) => setGroup(e.target.value)} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="d-block mb-2"><strong>Bosqich</strong></label>
                                        <div className="d-flex" style={{ gap: 10, flexWrap: "wrap" }}>
                                            {Object.keys(stages).map((s) => (
                                                <button key={s} onClick={() => setStage(s)} style={{
                                                    flex: 1, padding: "12px", borderRadius: 8, cursor: "pointer",
                                                    border: stage === s ? "2px solid #ffb606" : "1px solid #ddd",
                                                    background: stage === s ? "#fff8e6" : "#fff", fontWeight: 600
                                                }}>
                                                    {stages[s].label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    {existing && existing[stage] && (
                                        <p style={{ color: "#f59e0b", fontSize: 14 }}>
                                            Bu talaba {stages[stage].label.toLowerCase()}ni avval topshirgan ({existing[stage].overall}%). Qayta topshirilsa, natija yangilanadi.
                                        </p>
                                    )}
                                    {stage === "post" && name.trim() && !(existing && existing.pre) && (
                                        <p style={{ color: "#ef4444", fontSize: 14 }}>
                                            Bu F.I.Sh va guruh uchun kirish diagnostikasi topilmadi. Taqqoslash uchun kirish bosqichidagi ma'lumotlarni aynan shunday kiriting.
                                        </p>
                                    )}
                                    <button
                                        onClick={() => { setStarted(true); window.scrollTo(0, 0); }}
                                        disabled={!name.trim() || !group.trim()}
                                        className="eduhive-btn mt-2"
                                        style={{ border: "none", opacity: !name.trim() || !group.trim() ? 0.5 : 1 }}
                                    >
                                        <span>Diagnostikani boshlash</span>
                                        <span className="eduhive-btn__icon">
                                            <span className="eduhive-btn__icon__inner"><i className="icon-right-arrow"></i></span>
                                        </span>
                                    </button>
                                </div>
                                <div className="col-lg-6">
                                    {components.map((c) => (
                                        <div key={c.id} style={{ display: "flex", gap: 14, marginBottom: 16 }}>
                                            <div style={{ fontSize: 22, color: "#ffb606", minWidth: 28 }}><i className={c.icon}></i></div>
                                            <div>
                                                <strong>{c.title}</strong>
                                                <p style={{ margin: 0, color: "#666", fontSize: 14 }}>{c.description} ({c.items.length} ta savol)</p>
                                            </div>
                                        </div>
                                    ))}
                                    <p style={{ fontSize: 13, color: "#999" }}>
                                        Darajalar: {levels.map((l, i) => `${l.label.toLowerCase()} (${l.min}–${levels[i + 1] ? levels[i + 1].min - 1 : 100}%)`).join(", ")}.
                                    </p>
                                </div>
                            </div>

                            {records.length > 0 && (
                                <div style={{ marginTop: 50, paddingBottom: 30 }}>
                                    <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: 10, flexWrap: "wrap" }}>
                                        <h4 style={{ margin: 0 }}>Shu qurilmadagi natijalar ({records.length} ta talaba)</h4>
                                        <button onClick={exportCsv} className="eduhive-btn" style={{ border: "none" }}>
                                            <span>Excel (CSV) ga yuklash</span>
                                        </button>
                                    </div>
                                    <div className="row gutter-y-20 mb-3">
                                        {Object.keys(stages).map((s) => (
                                            <div className="col-md-6" key={s}>
                                                <div style={{ background: "#fafafa", borderRadius: 12, padding: 16 }}>
                                                    <strong>{stages[s].label}: darajalar taqsimoti</strong>
                                                    <div className="d-flex mt-2" style={{ gap: 12, flexWrap: "wrap" }}>
                                                        {distribution(s).map((l) => (
                                                            <span key={l.id} style={{ color: l.color, fontWeight: 600 }}>
                                                                {l.label}: {l.count} ({l.percent}%)
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div style={{ overflowX: "auto" }}>
                                        <table className="table" style={{ minWidth: 600 }}>
                                            <thead>
                                                <tr>
                                                    <th>F.I.Sh</th>
                                                    <th>Guruh</th>
                                                    <th>Kirish</th>
                                                    <th>Chiqish</th>
                                                    <th>O'sish</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {records.map(([k, r]) => (
                                                    <tr key={k}>
                                                        <td>{r.name}</td>
                                                        <td>{r.group}</td>
                                                        <td>{r.pre ? <>{r.pre.overall}% <LevelBadge percent={r.pre.overall} /></> : "—"}</td>
                                                        <td>{r.post ? <>{r.post.overall}% <LevelBadge percent={r.post.overall} /></> : "—"}</td>
                                                        <td>{r.pre && r.post ? `${r.post.overall - r.pre.overall > 0 ? "+" : ""}${r.post.overall - r.pre.overall}%` : "—"}</td>
                                                        <td>
                                                            <button
                                                                onClick={() => window.confirm(`${r.name} natijalari o'chirilsinmi?`) && setAll(removeStudent(k))}
                                                                style={{ border: "none", background: "none", color: "#ef4444", cursor: "pointer" }}
                                                                title="O'chirish"
                                                            >
                                                                <i className="icon-cross"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

export default Diagnostics;
